
import React, { useEffect } from 'react';
import { collection, getDocs, db } from '../firebase';

/**
 * AssetPrefetcher Component
 * Handles background caching of critical images to improve performance on subsequent visits.
 * Focuses on Profile, Cashback, Ads, and Shop images as requested.
 */
export const AssetPrefetcher: React.FC = () => {
    useEffect(() => {
        const prefetchAssets = async () => {
            // Wait for the browser to be idle to avoid impacting initial load performance
            if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(() => startCaching());
            } else {
                setTimeout(startCaching, 3000);
            }
        };

        const startCaching = async () => {
            try {
                const cache = await caches.open('rashid-online-assets-v1');
                const urlsToCache: string[] = [
                    // Constants/Placeholders
                    'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400', // Default profile
                    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800', // Default shop
                ];

                // 1. Fetch Ads
                const adsSnap = await getDocs(collection(db, 'ads'));
                adsSnap.forEach(doc => {
                    const data = doc.data();
                    if (data.image) urlsToCache.push(data.image);
                });

                // 2. Fetch Shops (Logos and Banners)
                const shopsSnap = await getDocs(collection(db, 'stores'));
                shopsSnap.forEach(doc => {
                    const data = doc.data();
                    if (data.logo) urlsToCache.push(data.logo);
                    if (data.banner) urlsToCache.push(data.banner);
                    if (data.coverImage) urlsToCache.push(data.coverImage);
                });

                // 3. Current User Profile Photo (Already handled by browser usually, but for consistency)
                // We don't have user context here easily without adding more props, 
                // but we can target common assets.

                // Filter duplicates and valid URLs
                const uniqueUrls = [...new Set(urlsToCache)].filter(u => u && u.startsWith('http'));

                // Silently cache each URL
                // We use a slow trickle to avoid network congestion
                for (const url of uniqueUrls) {
                    try {
                        // Check if already in cache
                        const match = await cache.match(url);
                        if (!match) {
                            // Fetch and cache
                            await cache.add(url);
                            // Small delay between requests to be extra gentle
                            await new Promise(r => setTimeout(r, 200));
                        }
                    } catch (e) {
                        // Ignore individual fetch errors (e.g. CORS or 404)
                        console.debug(`Prefetch failed for ${url}:`, e);
                    }
                }

                console.log(`[AssetPrefetcher] Successfully cached ${uniqueUrls.length} critical assets in background.`);
            } catch (err) {
                console.error('[AssetPrefetcher] Error:', err);
            }
        };

        prefetchAssets();
    }, []);

    return null; // This component doesn't render anything
};
